/**
 * Default video-analysis model — matches the backend `defaultVideoUnderstandModel`
 * constant. Always trusted server-side, so it needs no capability check.
 */
export const DEFAULT_VIDEO_ANALYSIS_MODEL = 'gemini-3.6-flash';

/** localStorage key the Settings panel persists the chosen video-analysis model under. */
export const VIDEO_ANALYSIS_MODEL_STORAGE_KEY = 'videoAnalysisModel';

/** The default until 2026-09-29. */
const PREVIOUS_DEFAULT_VIDEO_ANALYSIS_MODEL = 'xiaomi/mimo-v2-omni';
const DEFAULT_MIGRATED_STORAGE_KEY = 'videoAnalysisModelDefaultMigrated';

/**
 * The Settings modal is always mounted and `useLocalStorage` persists its
 * default on mount, so every browser holds the previous default as if someone
 * had picked it. Once per browser, that stored value becomes the new default;
 * a later deliberate pick of the old model sticks.
 */
export const migrateVideoAnalysisModelDefault = (): void => {
  try {
    if (localStorage.getItem(DEFAULT_MIGRATED_STORAGE_KEY)) return;
    const raw = localStorage.getItem(VIDEO_ANALYSIS_MODEL_STORAGE_KEY);
    if (raw && JSON.parse(raw) === PREVIOUS_DEFAULT_VIDEO_ANALYSIS_MODEL) {
      localStorage.setItem(VIDEO_ANALYSIS_MODEL_STORAGE_KEY, JSON.stringify(DEFAULT_VIDEO_ANALYSIS_MODEL));
    }
    localStorage.setItem(DEFAULT_MIGRATED_STORAGE_KEY, '1');
  } catch {
    /* storage unavailable or unparseable: leave it alone */
  }
};

// Runs on import, before the Settings modal or the Builder reads the key.
migrateVideoAnalysisModelDefault();

/**
 * Reads the persisted video-analysis model, falling back to the default when
 * unset or unparseable. Read at analysis time (not cached in React state) so the
 * freshest setting is always used — the Settings modal and the Builder view live
 * in separate component trees. Capability is validated server-side, so no
 * client-side allowlist check is needed here.
 */
export const getStoredVideoAnalysisModel = (): string => {
  try {
    const raw = localStorage.getItem(VIDEO_ANALYSIS_MODEL_STORAGE_KEY);
    if (!raw) return DEFAULT_VIDEO_ANALYSIS_MODEL;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string' && parsed.trim() !== '') return parsed;
  } catch {
    /* fall through to default */
  }
  return DEFAULT_VIDEO_ANALYSIS_MODEL;
};
